import { clampFreeText } from './clamp-text.js';

/**
 * Gründe für ein Storno an der Kasse (U17d, Plan 16 Abschnitt 4.10.6). Eine Quelle für Server und Kasse.
 *
 * Der Grund gewährt nichts (keine Rechte) und lehnt nie ein Storno ab: Der Server speichert, was die Kasse sendet. Die Pflicht
 * zur Auswahl prüft nur die Oberfläche.
 */
export const VOID_REASONS = [
  'customer_return',
  'wrong_item',
  'double_scan',
  'wrong_price',
  'not_paid',
  'other',
  'correction',
  'register_voided',
] as const;
export type VoidReason = (typeof VOID_REASONS)[number];

/** Längster Freitext zu einem Storno (Zeichen). Der Text ist für die Leitung gedacht und enthält keine Personendaten. */
export const VOID_NOTE_MAX = 200;

/** Klartext je Grund für Kasse, Verkaufsübersicht und Export. */
export const VOID_REASON_LABELS: Readonly<Record<VoidReason, string>> = {
  customer_return: 'Kunde gibt zurück',
  wrong_item: 'Falscher Artikel erfasst',
  double_scan: 'Doppelt erfasst',
  wrong_price: 'Falscher Preis',
  not_paid: 'Kunde hat nicht bezahlt',
  other: 'Anderer Grund',
  correction: 'Korrigiert',
  register_voided: 'Von der Basar-Leitung storniert',
};

/** Anzeige, wenn eine ältere Kasse keinen Grund gesendet hat. */
export const VOID_REASON_UNKNOWN_LABEL = 'ohne Angabe (ältere Kasse)';

/** Gründe, die die Kassenkraft wählen kann (in dieser Reihenfolge als Chips). `correction` und `register_voided` setzt das System. */
export const SELECTABLE_VOID_REASONS = [
  'customer_return',
  'wrong_item',
  'double_scan',
  'wrong_price',
  'not_paid',
  'other',
] as const satisfies readonly VoidReason[];
export type SelectableVoidReason = (typeof SELECTABLE_VOID_REASONS)[number];

export function isVoidReason(value: unknown): value is VoidReason {
  return typeof value === 'string' && (VOID_REASONS as readonly string[]).includes(value);
}

/** Klartext für einen gespeicherten Grund; `null`/unbekannt ergibt „ohne Angabe“. */
export function voidReasonLabel(reason: string | null | undefined): string {
  return isVoidReason(reason) ? VOID_REASON_LABELS[reason] : VOID_REASON_UNKNOWN_LABEL;
}

/** Kürzt den Freitext auf `VOID_NOTE_MAX` Zeichen (nach Leerraum-Bereinigung); leer ergibt `undefined`. */
export function clampVoidNote(text: string | undefined): string | undefined {
  return clampFreeText(text, VOID_NOTE_MAX);
}

/** Klartext mit Freitext der Kasse für Verkaufsübersicht und Export: „Anderer Grund: Kunde hat es sich anders überlegt“. */
export function voidReasonText(
  reason: string | null | undefined,
  note: string | null | undefined,
): string {
  const label = voidReasonLabel(reason);
  const text = note?.trim() ?? '';
  return text === '' ? label : `${label}: ${text}`;
}
